import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const IMAGE_BASE = 'https://marketing-static.signnow.com/349/images/product/e-signature-v2/';
const IMAGE_BASE_V2 = 'https://marketing-static.signnow.com/345/images/product/e-signature-v2/';

const IMAGE_NAMES: Record<string, string> = {
  yellow: 'esign-wherever-you-are.svg',
  violet: 'secure-signing-is-our-priority.svg',
  pink: 'collect-signatures-on-the-first-try.svg',
};

/**
 * Colored tiles feature section for SignNow brand.
 * Displays a 2x2 grid of colored cards each with an illustration, title, and description.
 * The section heading is inline-editable via the `heading` slot.
 * Card content (color, title, description) is managed via the `items` prop and editable
 * in the visual editor right panel.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Colored tiles section with four feature cards for small teams and individuals
 * @slot heading - {required}, <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @example
 *   <sn-features-type-3
 *     items='[{"color":"yellow","title":"eSign from anywhere","description":"Upload documents from your device or cloud and add your signature with ease: draw, upload, or type it on your mobile device or laptop."},{"color":"green","title":"Prepare documents for sending","description":"Drag and drop fillable fields on your document and assign them to recipients. Reduce document errors and delight clients with an intuitive signing process."},{"color":"violet","title":"Secure signing is our priority","description":"Secure your documents by setting two-factor signer authentication. View who made changes and when in your document with the court-admissible Audit Trail."},{"color":"pink","title":"Collect signatures on the first try","description":"Define a signing order, configure reminders for signers, and set your document expiration date. signNow will send you instant updates once your document is signed."}]'
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2">Simplified document journeys for small teams and individuals</me-heading>
 *   </sn-features-type-3>
 */
@Component({
  tag: 'sn-features-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFeaturesType3 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Feature cards data
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} color - Card color theme (yellow, green, violet, pink)
   * @item.property {string} title - Card heading text
   * @item.property {string} description - Card description text
   * @item.required color
   * @item.required title
   * @item.required description
   * @item.content title description
   * @minItems 1
   * @maxItems 4
   */
  @Prop({ mutable: true }) items!: any[] | string;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, [
      { color: 'yellow', title: 'eSign from anywhere', description: 'Upload documents from your device or cloud and add your signature with ease: draw, upload, or type it on your mobile device or laptop.' },
      { color: 'green', title: 'Prepare documents for sending', description: 'Drag and drop fillable fields on your document and assign them to recipients. Reduce document errors and delight clients with an intuitive signing process.' },
      { color: 'violet', title: 'Secure signing is our priority', description: 'Secure your documents by setting two-factor signer authentication. View who made changes and when in your document with the court-admissible Audit Trail.' },
      { color: 'pink', title: 'Collect signatures on the first try', description: "Define a signing order, configure reminders for signers, and set your document's expiration date. signNow will send you instant updates once your document is signed." },
    ]);
  }

  render() {
    const cards = Array.isArray(this.items) ? this.items : [];
    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class="features-type-3" yGap="2xl" yGapLg="5xl">
          <me-row direction="column">
            <me-column spanLg="12" class="features-type-3__heading-column">
              <slot name="heading"></slot>
            </me-column>
            <me-column spanLg="12" class="features-type-3__cards">
              {cards.map((card, index) => (
                <div class={`features-type-3__card features-type-3__card--${card.color}`} key={String(index)}>
                  <div class="features-type-3__card-image">
                    <me-picture
                      src={card.color === 'green' ? undefined : `${IMAGE_BASE}${IMAGE_NAMES[card.color]}`}
                      srcSet={card.color === 'green' ? `${IMAGE_BASE_V2}prepare-documents-for-sending.png 1x, ${IMAGE_BASE_V2}prepare-documents-for-sending@2x.png 2x` : undefined}
                      webpSrcSet={card.color === 'green' ? `${IMAGE_BASE_V2}prepare-documents-for-sending.webp 1x, ${IMAGE_BASE_V2}prepare-documents-for-sending@2x.webp 2x` : undefined}
                      alt=""
                      aspectRatio="auto"
                      lazy={true}
                    />
                  </div>
                  <div class="features-type-3__card-content">
                    <me-heading variant="4" tag="h3" class="features-type-3__card-title">
                      {card.title}
                    </me-heading>
                    <me-text class="features-type-3__card-description">{card.description}</me-text>
                  </div>
                </div>
              ))}
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
